import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Slider } from "../ui/slider";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
    Math.round(n)
  );

const emiFor = (p, annualRate, years) => {
  const r = annualRate / 1200;
  const n = years * 12;
  if (r === 0) return p / n;
  const f = Math.pow(1 + r, n);
  return (p * r * f) / (f - 1);
};

const loanProfiles = {
  "Home Loan": { rate: 8.5, years: 20, foir: 0.55 },
  "Business Loan": { rate: 12.5, years: 5, foir: 0.5 },
  "Loan Against Property": { rate: 9.5, years: 15, foir: 0.5 },
  "Gold Loan": { rate: 10.5, years: 3, foir: 0.4 },
};

const EmiTab = () => {
  const [amount, setAmount] = useState(5000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const emi = useMemo(() => emiFor(amount, rate, years), [amount, rate, years]);
  const total = emi * years * 12;
  const interest = total - amount;
  const chartData = [
    { name: "Principal", value: Math.round(amount) },
    { name: "Interest", value: Math.round(interest) },
  ];

  const sliders = [
    { label: "Loan amount", value: amount, set: setAmount, min: 100000, max: 100000000, step: 100000, fmt: inr, id: "emi-amount" },
    { label: "Interest rate (p.a.)", value: rate, set: setRate, min: 6, max: 20, step: 0.1, fmt: (v) => `${v.toFixed(1)}%`, id: "emi-rate" },
    { label: "Tenure", value: years, set: setYears, min: 1, max: 30, step: 1, fmt: (v) => `${v} yrs`, id: "emi-tenure" },
  ];

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className="space-y-10">
        {sliders.map((s) => (
          <div key={s.id}>
            <div className="flex justify-between items-baseline mb-4">
              <Label className="text-sm text-[#A0AAB2]">{s.label}</Label>
              <span data-testid={`${s.id}-value`} className="font-serif text-xl text-gold">{s.fmt(s.value)}</span>
            </div>
            <Slider
              data-testid={`${s.id}-slider`}
              value={[s.value]}
              onValueChange={(v) => s.set(v[0])}
              min={s.min}
              max={s.max}
              step={s.step}
            />
          </div>
        ))}
      </div>
      <div className="bg-slate3 border border-white/10 p-8">
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Monthly EMI</p>
        <p data-testid="emi-result" className="font-serif text-4xl sm:text-5xl text-gold mt-2">{inr(emi)}</p>
        <div className="grid grid-cols-2 gap-6 mt-8 text-sm">
          <div>
            <p className="text-muted-foreground">Total interest</p>
            <p data-testid="emi-total-interest" className="text-foreground font-medium mt-1">{inr(interest)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Total payable</p>
            <p data-testid="emi-total-payable" className="text-foreground font-medium mt-1">{inr(total)}</p>
          </div>
        </div>
        <div className="h-56 mt-6" data-testid="emi-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} dataKey="value" innerRadius={55} outerRadius={85} stroke="none" paddingAngle={3}>
                <Cell fill="#E8B33D" />
                <Cell fill="#2c3947" />
              </Pie>
              <Tooltip
                formatter={(v, name) => [inr(v), name]}
                contentStyle={{ background: "#151B22", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2 }}
                itemStyle={{ color: "#F8F9FA" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex gap-6 justify-center text-xs text-muted-foreground">
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-gold inline-block" /> Principal</span>
          <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 bg-[#2c3947] inline-block" /> Interest</span>
        </div>
      </div>
    </div>
  );
};

const EligibilityTab = () => {
  const [income, setIncome] = useState("150000");
  const [existing, setExisting] = useState("10000");
  const [type, setType] = useState("Home Loan");

  const result = useMemo(() => {
    const profile = loanProfiles[type];
    const inc = parseFloat(income) || 0;
    const emis = parseFloat(existing) || 0;
    const maxEmi = Math.max(inc * profile.foir - emis, 0);
    const r = profile.rate / 1200;
    const n = profile.years * 12;
    const f = Math.pow(1 + r, n);
    const maxLoan = maxEmi > 0 ? (maxEmi * (f - 1)) / (r * f) : 0;
    return { maxEmi, maxLoan, profile };
  }, [income, existing, type]);

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className="space-y-8">
        <div>
          <Label htmlFor="elig-type" className="text-sm text-[#A0AAB2] mb-2 block">Loan type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="elig-loan-type" id="elig-type" className="bg-slate3 border-white/10 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate2 border-white/10">
              {Object.keys(loanProfiles).map((k) => (
                <SelectItem key={k} value={k} data-testid={`elig-type-${k.toLowerCase().replace(/\s/g, "-")}`}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="elig-income" className="text-sm text-[#A0AAB2] mb-2 block">Net monthly income (₹)</Label>
          <Input
            data-testid="elig-income-input"
            id="elig-income"
            type="number"
            min="0"
            value={income}
            onChange={(e) => setIncome(e.target.value)}
            className="bg-slate3 border-white/10 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="elig-existing" className="text-sm text-[#A0AAB2] mb-2 block">Existing EMIs per month (₹)</Label>
          <Input
            data-testid="elig-existing-input"
            id="elig-existing"
            type="number"
            min="0"
            value={existing}
            onChange={(e) => setExisting(e.target.value)}
            className="bg-slate3 border-white/10 rounded-sm"
          />
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Indicative only. Assumes {result.profile.rate}% p.a. over {result.profile.years} years and a
          {" "}{Math.round(result.profile.foir * 100)}% fixed-obligation-to-income ratio. Final eligibility depends on
          lender policy, credit score and documentation.
        </p>
      </div>
      <div className="bg-slate3 border border-white/10 p-8">
        <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Indicative eligibility</p>
        <p data-testid="elig-result" className="font-serif text-4xl sm:text-5xl text-gold mt-2">
          {inr(result.maxLoan)}
        </p>
        <div className="mt-8 text-sm">
          <p className="text-muted-foreground">Maximum affordable EMI</p>
          <p data-testid="elig-max-emi" className="text-foreground font-medium mt-1">{inr(result.maxEmi)} / month</p>
        </div>
        <a
          data-testid="elig-enquire-button"
          href="#enquire"
          className="mt-10 inline-block bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-6 py-3 text-sm font-semibold"
        >
          Get a precise quote
        </a>
      </div>
    </div>
  );
};

export const Calculators = () => (
  <section id="calculators" data-testid="calculators-section" className="py-14 lg:py-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="max-w-2xl mb-14"
      >
        <p className="overline-label mb-4">Run the numbers</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
          Know before you borrow.
        </h2>
      </motion.div>

      <Tabs defaultValue="emi" data-testid="calculator-tabs">
        <TabsList className="bg-slate2 border border-white/10 rounded-sm mb-12">
          <TabsTrigger data-testid="tab-emi" value="emi" className="rounded-sm data-[state=active]:bg-gold data-[state=active]:text-night">
            EMI Calculator
          </TabsTrigger>
          <TabsTrigger data-testid="tab-eligibility" value="eligibility" className="rounded-sm data-[state=active]:bg-gold data-[state=active]:text-night">
            Eligibility Checker
          </TabsTrigger>
        </TabsList>
        <TabsContent value="emi"><EmiTab /></TabsContent>
        <TabsContent value="eligibility"><EligibilityTab /></TabsContent>
      </Tabs>
    </div>
  </section>
);
